import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  SortByAlpha as SortByAlphaIcon,
} from '@mui/icons-material';
import PatientSearchBar from './PatientSearchBar';
import PatientForm from './PatientForm';
import { useTherapistHistoryQuery } from '../../stores/tanstack/queries/useTherapistHistoryQuery';

const PatientTable = ({ onPatientClick, onPatientAdded }) => {
  const { t } = useTranslation();  // Translation hook

  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);

  // Sorting and filtering states
  const [sortBy, setSortBy] = useState('duration-asc');
  const [filterStatus, setFilterStatus] = useState('all');

  const LOAD_MORE_COUNT = 6;

  const { clients, isLoading, isFetching, error, refetch } = useTherapistHistoryQuery();

  // Transform API data
  const transformedPatients = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];

    return clients.map(client => ({
      id: client.id,
      name: `${client.firstName} ${client.lastName}`,
      email: client.userEmail,
      status: client.status === 'NEW' ? t('new') : client.status === 'ACTIVE' ? t('active') : t('inactive'),
      videos: client.videos?.length || 0,
      numberOfWeeks: client.numberOfWeeks || 0,
      numberOfDays: client.numberOfDays || 0,
      numberOfMonths: client.numberOfMonths || 0,
      lastAssignedAt: client.lastAssignedAt ? new Date(client.lastAssignedAt) : null,
      videosData: client.videos || [],
      firstName: client.firstName || '',
      lastName: client.lastName || '',
    }));
  }, [clients, t]);

  // Apply filtering
  const filteredPatients = useMemo(() => {
    let filtered = transformedPatients;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(patient => patient.status === filterStatus);
    }

    if (searchQuery) {
      filtered = filtered.filter(patient =>
        patient.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [transformedPatients, filterStatus, searchQuery]);

  // Apply sorting
  const sortedPatients = useMemo(() => {
    let sorted = [...filteredPatients];

    switch (sortBy) {
      case 'original':
        sorted = filteredPatients;
        break;
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'duration-asc':
        sorted.sort((a, b) => {
          if (!a.lastAssignedAt && !b.lastAssignedAt) return 0;
          if (!a.lastAssignedAt) return 1;
          if (!b.lastAssignedAt) return -1;
          return b.lastAssignedAt - a.lastAssignedAt; // most recently assigned first
        });
        break;
      case 'duration-desc':
        sorted.sort((a, b) => {
          if (!a.lastAssignedAt && !b.lastAssignedAt) return 0;
          if (!a.lastAssignedAt) return 1;
          if (!b.lastAssignedAt) return -1;
          return a.lastAssignedAt - b.lastAssignedAt; // oldest assigned first
        });
        break;
    }

    return sorted;
  }, [filteredPatients, sortBy]);

  const visiblePatients = useMemo(() => {
    return sortedPatients.slice(0, visibleCount);
  }, [sortedPatients, visibleCount]);

  const hasMorePatients = visibleCount < sortedPatients.length;
  const remainingPatients = sortedPatients.length - visibleCount;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + LOAD_MORE_COUNT);
  };

  const handleShowLess = () => {
    setVisibleCount(6);
  };

  const handleAddPatient = (formData) => {
    console.log('New patient data:', formData);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case t('active'):
        return { bg: '#D1FAE5', color: '#059669' };
      case t('new'):
        return { bg: '#DBEAFE', color: '#2563EB' };
      case t('inactive'):
        return { bg: '#F3F4F6', color: '#6B7280' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280' };
    }
  };

  React.useEffect(() => {
    setVisibleCount(6);
  }, [searchQuery, filterStatus, sortBy]);

  return (
    <>
      <Paper sx={{ borderRadius: 1, p: 4, background: 'white', boxShadow: 'none', border: '1px solid #E5E7EB' }}>
        {/* Search Bar and Add Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <PatientSearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              bgcolor: '#2563EB',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#1D4ED8' },
            }}
          >
            {t('addNewPatient')}
          </Button>
        </Box>

        {/* Filters and Sorting Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'end', bgcolor: '#ffffff', p: 2 }}>
          {/* Sort By Name */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: '0.8rem', '&.Mui-focused': { color: '#2563EB' }, '&.MuiInputLabel-shrink': { color: '#2563EB' } }}>
              {t('sortByName')}
            </InputLabel>
            <Select
              value={sortBy.startsWith('name') ? sortBy : 'name-asc'}
              onChange={(e) => setSortBy(e.target.value)}
              label={t('sortByName')}
              sx={{
                height: 40, color: 'rgba(0, 0, 0, 0.9)', bgcolor: '#fafafa', '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563EB',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563EB !important',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(0, 0, 0, 0.5)',
                },
                '& .MuiSelect-select': {
                  color: 'rgba(0, 0, 0, 0.9)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    bgcolor: 'white',
                    '& .MuiMenuItem-root': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="name-asc">{t('nameAsc')}</MenuItem>
              <MenuItem value="name-desc">{t('nameDesc')}</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Duration */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: '0.8rem', '&.Mui-focused': { color: '#2563EB' }, '&.MuiInputLabel-shrink': { color: '#2563EB' } }}>
              {t('sortByDuration')}
            </InputLabel>
            <Select
              value={sortBy.startsWith('duration') ? sortBy : 'duration-asc'}
              onChange={(e) => setSortBy(e.target.value)}
              label={t('sortByDuration')}
              sx={{
                height: 40, color: 'rgba(0, 0, 0, 0.9)', bgcolor: '#fafafa', '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563EB',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563EB !important',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(0, 0, 0, 0.5)',
                },
                '& .MuiSelect-select': {
                  color: 'rgba(0, 0, 0, 0.9)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    bgcolor: 'white',
                    '& .MuiMenuItem-root': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="duration-asc">{t('durationShortFirst')}</MenuItem>
              <MenuItem value="duration-desc">{t('durationLongFirst')}</MenuItem>
            </Select>
          </FormControl>

          {/* Filter By Status */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: '0.8rem', '&.Mui-focused': { color: '#2563EB' }, '&.MuiInputLabel-shrink': { color: '#2563EB' } }}>
              {t('filterStatus')}
            </InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label={t('filterStatus')}
              sx={{
                height: 40, color: 'rgba(0, 0, 0, 0.9)', bgcolor: '#fafafa', '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563EB',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563EB !important',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(0, 0, 0, 0.5)',
                },
                '& .MuiSelect-select': {
                  color: 'rgba(0, 0, 0, 0.9)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    bgcolor: 'white',
                    '& .MuiMenuItem-root': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">{t('all')}</MenuItem>
              <MenuItem value={t('new')}>{t('onlyNew')}</MenuItem>
              <MenuItem value={t('active')}>{t('onlyActive')}</MenuItem>
              <MenuItem value={t('inactive')}>{t('onlyInactive')}</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {(filterStatus !== 'all' || sortBy !== 'name-asc' || searchQuery) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilterStatus('all');
                setSortBy('name-asc');
                setSearchQuery('');
              }}
              sx={{
                height: 40,
                minWidth: 100,
                borderColor: 'rgba(0, 0, 0, 0.2)',
                color: 'white',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
                bgcolor: '#1D4ED8',
                '&:hover': {
                  borderColor: '#2563EB',
                  color: '#2563EB',
                  bgcolor: 'rgba(37, 99, 235, 0.04)',
                },
              }}
            >
              {t('clearAll')}
            </Button>
          )}
        </Box>

        {/* Header with patient count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#1a1f3a', fontWeight: 600, fontSize: '1.1rem' }}>
            {t('patientList')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
            {t('showingCount', { visible: visiblePatients.length, total: sortedPatients.length })}
            {filterStatus !== 'all' && ` (${t('filteredBy', { status: filterStatus })})`}
          </Typography>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2, color: '#6B7280' }}>
              {t('loadingPatients')}
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={() => refetch()}>{t('retry')}</Button>}>
            {t('failedLoadPatients', { message: error.message })}
          </Alert>
        )}

        {/* Table */}
        {!isLoading && !error && (
          <>
            {isFetching && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {t('updating')}
                </Typography>
              </Box>
            )}

            <TableContainer>
              <Table sx={{ '& .MuiTableCell-root': { borderBottom: 'none', textAlign: 'center' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
                      {t('name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
                      {t('email')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
                      {t('status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
                      {t('videos')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
                      {t('treatmentDuration')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
                      {t('lastAssignedAt')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePatients.length > 0 ? (
                    visiblePatients.map((patient, index) => {
                      const statusStyle = getStatusColor(patient.status);
                      return (
                        <TableRow
                          key={patient.id}
                          onClick={() => onPatientClick(patient)}
                          sx={{
                            cursor: 'pointer',
                            borderBottom: index !== visiblePatients.length - 1 ? '1px solid #E5E7EB' : 'none',
                            '&:hover': { background: '#F9FAFB' },
                          }}
                        >
                          <TableCell sx={{ py: 2.5, color: '#1a1f3a', fontWeight: 500, textAlign: 'center' }}>{patient.name}</TableCell>
                          <TableCell sx={{ py: 2.5, color: '#6B7280', textAlign: 'center' }}>{patient.email}</TableCell>
                          <TableCell sx={{ py: 2.5, textAlign: 'center' }}>
                            <Chip
                              label={patient.status}
                              size="small"
                              sx={{ background: statusStyle.bg, color: statusStyle.color, fontWeight: 500, fontSize: '0.875rem', height: 28 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2.5, color: '#1a1f3a', fontWeight: 500, textAlign: 'center' }}>{patient.videos}</TableCell>
                          <TableCell sx={{ py: 2.5, color: '#1a1f3a', fontWeight: 500, textAlign: 'center' }}>{patient.numberOfWeeks}</TableCell>
                          <TableCell sx={{ py: 2.5, color: '#6B7280', textAlign: 'center' }}>
                            {patient.lastAssignedAt ? patient.lastAssignedAt.toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#6B7280' }}>
                          {searchQuery || filterStatus !== 'all'
                            ? t('noPatientsFound')
                            : t('noPatientsYet')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Load More / Show Less Buttons */}
            {visiblePatients.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                {hasMorePatients && (
                  <Button
                    variant="outlined"
                    endIcon={<ExpandMoreIcon />}
                    onClick={handleLoadMore}
                    sx={{
                      borderColor: '#2563EB',
                      color: '#2563EB',
                      px: 4,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#1D4ED8', bgcolor: '#EFF6FF' },
                    }}
                  >
                    {t('loadMore', { count: remainingPatients })}
                  </Button>
                )}
                {visibleCount > 6 && (
                  <Button
                    variant="text"
                    onClick={handleShowLess}
                    sx={{
                      color: '#6B7280',
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#F3F4F6' },
                    }}
                  >
                    {t('showLess')}
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>

      <PatientForm
        open={dialogOpen}
        onPatientAdded={onPatientAdded}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleAddPatient}
      />
    </>
  );
};

export default PatientTable;